export const ROUTE_PATHS = {
  HOME: '/',
  LOGIN: '/login',
  DASHBOARD: '/dashboard',
  CONDOMINI: '/condomini',
  CONDOMINIO_DASHBOARD: '/condomini/:id/dashboard',
  DOCUMENTI: '/documenti',
  ANALISI: '/analisi',
  ANALISI_DETAIL: '/analisi/:id',
  REPORT_STAMPA: '/analisi/:id/report',
  ADMIN: '/admin',
  PORTALE_CONDOMINI: '/portale-condomini',
  PROFILE: '/profile',
} as const;

export interface Profile {
  id: string;
  email: string;
  full_name: string;
  role: 'admin' | 'superadmin';
  condomini_ids?: string[];
  created_at?: string;
}

export interface Condominio {
  id: string;
  nome: string;
  indirizzo: string;
  cf?: string;
  iban?: string;
  amministratore?: string;
  anno_costruzione?: number;
  num_unita?: number;
  user_id: string;
  created_at: string;
  // Anagrafica estesa
  assicurazione_compagnia?: string;
  assicurazione_polizza?: string;
  ascensore_ditta?: string;
  ascensore_matricola?: string;
  impianti_termici_ditta?: string;
  verifica_elevatori?: string;
  verifica_acqua?: string;
  note?: string;
}

export interface Documento {
  id: string;
  condominio_id: string;
  tipo: 'consuntivo' | 'preventivo';
  anno: number;
  storage_path: string;  // nome reale colonna DB
  file_path?: string;    // alias legacy (può essere vuoto)
  nome_file: string;
  file_size?: number;
  stato: 'caricato' | 'elaborazione' | 'analizzato' | 'errore';
  condominio_nome?: string;
  created_at: string;
}

export interface Analisi {
  id: string;
  condominio_id: string;
  documento_id?: string;
  anno: number;
  tipo_gestione?: string;
  titolo: string;
  stato: 'bozza' | 'elaborazione' | 'completata' | 'errore';
  totale_entrate: number;
  totale_uscite: number;
  totale_preventivo?: number;
  nota_sintetica?: string;  // colonna aggiunta
  nota_sintesi?: string;    // colonna originale DB
  nome_condominio?: string;
  created_at: string;
  updated_at?: string;
}

export interface VoceSpesa {
  id: string;
  analisi_id: string;
  codice?: string | null;
  descrizione: string;
  categoria: string;
  macro_categoria: string;
  // Colonne reali DB (NON esistono 'consuntivo' e 'preventivo' nella tabella)
  importo_consuntivo: number;
  importo_preventivo: number;
  scostamento_euro: number;
  scostamento_pct: number | null;
  scostamento_percentuale?: number | null;
  is_non_preventivata: boolean;
}

export interface MacroCategoria {
  nome: string;
  voci: VoceSpesa[];
  totale_consuntivo: number;
  totale_preventivo: number;
  scostamento_euro: number;
  scostamento_pct: number | null;
}

export interface AnalisiKPI {
  id: string;
  analisi_id: string;
  // Colonne reali DB
  label: string;           // nome colonna reale
  valore: string;
  dettaglio?: string | null;
  stato: 'ok' | 'attenzione' | 'critico';
  ordine: number;
  // Alias legacy
  titolo?: string;
  unita?: string;
}

export interface StatoPatrimoniale {
  id: string;
  analisi_id: string;
  // Colonne reali DB
  voce: string;             // nome colonna reale
  importo: number;
  tipo_sp: string;          // 'attivo' | 'passivo' (reale DB)
  ordine: number;
  // Alias legacy
  descrizione?: string;
  tipo?: string;
}

export interface AnalisiCondomino {
  id: string;
  analisi_id: string;
  nominativo: string;
  // Colonne reali DB
  totale_consuntivo: number;   // nome reale DB
  saldo_ordinario: number;     // nome reale DB
  vers_gest_corrente: number;  // nome reale DB
  saldo_finale: number;
  // Alias legacy
  piano_interno?: string;
  quota_ordinaria?: number;
  tot_consuntivo?: number;
  saldo_prec?: number;
  versamenti?: number;
}

export interface DettaglioSpesa {
  id: string;
  analisi_id: string;
  // Colonne reali DB
  voce: string;            // nome reale DB
  fornitore?: string | null;
  importo: number;
  nota?: string | null;
  ordine: number;
  // Alias legacy
  categoria?: string;
  descrizione?: string;
  importo_consuntivo?: number;
  importo_preventivo?: number;
  scostamento_euro?: number;
  scostamento_pct?: number | null;
}

export interface Raccomandazione {
  id: string;
  analisi_id: string;
  // Colonne reali DB
  titolo: string;          // nome reale DB
  testo: string;
  priorita: string;        // 'bassa' | 'media' | 'alta'
  ordine: number;
  // Alias legacy
  tipo?: string;
}

export interface InfoCondominio {
  nome?: string;
  indirizzo?: string;
  citta?: string;
  cf?: string;
  iban?: string;
  banca?: string;
  amministratore?: string;
  periodo?: string;
  n_unita?: number;
}

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatCurrencyCompact = (value: number): string => {
  if (Math.abs(value) >= 1000) {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  return formatCurrency(value);
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

export const formatPct = (value: number | null): string => {
  if (value === null) return 'N/D';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
};

export const calcScostamento = (
  consuntivo: number,
  preventivo: number
): { euro: number; percentuale: number | null } => {
  const euro = consuntivo - preventivo;
  const percentuale = preventivo !== 0 ? (euro / preventivo) * 100 : null;
  return { euro, percentuale };
};

export const groupVociByCategoria = (voci: VoceSpesa[]): MacroCategoria[] => {
  const grouped = new Map<string, MacroCategoria>();

  voci.forEach((voce) => {
    const macroNome = voce.macro_categoria;

    if (!grouped.has(macroNome)) {
      grouped.set(macroNome, {
        nome: macroNome,
        voci: [],
        totale_consuntivo: 0,
        totale_preventivo: 0,
        scostamento_euro: 0,
        scostamento_pct: null,
      });
    }

    const macro = grouped.get(macroNome)!;
    macro.totale_consuntivo += voce.importo_consuntivo ?? 0;
    macro.totale_preventivo += voce.importo_preventivo ?? 0;
    macro.voci.push(voce);
  });

  grouped.forEach((macro) => {
    const scost = calcScostamento(macro.totale_consuntivo, macro.totale_preventivo);
    macro.scostamento_euro = scost.euro;
    macro.scostamento_pct = scost.percentuale;
  });

  return Array.from(grouped.values()).sort((a, b) => a.nome.localeCompare(b.nome));
};